import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PricingPage from './pages/PricingPage';
import DashboardPage from './pages/DashboardPage';
import authService from './services/AuthService';
import LiveScanPage from './pages/LiveScanPage';
import LiveScanLandingPage from './pages/LiveScanLandingPage';
import UniversalLiveScanPage from './pages/UniversalLiveScanPage';
import Favorites from './pages/Favorites';
import StorefrontScanPage from './pages/StorefrontScanPage';
import A2ADealsPage from './pages/A2ADealsPage';
import WholesaleScanPage from './pages/WholesaleScanPage';
import ReverseScanPage from './pages/ReverseScanPage';
import SettingsPage from './pages/SettingsPage';
import PlanGate from './components/PlanGate';
import OnboardingModal from './components/OnboardingModal';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AIPStorefrontScanPage from './pages/AIPStorefrontScanPage';
import MyVideosDashboard from './pages/MyVideosDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReviewPromptModal from './components/ReviewPromptModal';
import CreatorConnectionsPage from './pages/CreatorConnectionsPage';
import MSPInventoryPage from './pages/MSPInventory/Mspinventorypage';
import A2WPage from './pages/A2WPage';
import WebinarsPage from './pages/WebinarsPage';
import HowIUseMSPPage from './pages/HowIUseMSPPage';
import CommunitiesPage from './pages/CommunitiesPage';
import InfluencerHunterPage from './pages/Influencerhunterpage';
import BrandImagesPage from './pages/BrandImagesPage';
import BrandDashboardPage from './pages/BrandDashboardPage';
import FreeAIPThumbnailMaker from './pages/Freeaipthumbnailmaker';
import FreeProductPhotoMaker from './pages/Freeproductphotomaker';
import MobileAppPage from './pages/Mobileapppage';
import BrandHunterPage from './pages/BrandHunterPage';
import LiveScanRetiredPage from './pages/Livescanretiredpage';
import MediaKitBuilderPage from './pages/MediaKitBuilderPage';
import MediaKitPublicPage from './pages/MediaKitPublicPage';
import MyKitsListPage from './pages/MyKitsListPage';
import CreateMediaKitPage from './pages/CreateMediaKitPage';
import AipCsvScanPage from './pages/AipCsvScanPage';
import MspGptPage from './pages/MspGptPage';

// Simple ProtectedRoute - just checks if user exists (no API call)
const SetSellerPro = () => {
  useEffect(() => {
    localStorage.setItem('preferredTool', 'seller');
  }, []);
  return <Navigate to="/" replace />;
};

const SetInfluencerPro = () => {
  useEffect(() => {
    localStorage.setItem('preferredTool', 'influencer');
  }, []);
  return <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children, user, requiredPlan = null }) => {
  const location = useLocation();

  if (!user) {
    // Pass the current path as "from" in state
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredPlan) {
    return (
      <PlanGate
        requiredPlan={requiredPlan}
        currentPlan={user.subscription_plan || 'No plan'}
        subscriptionActive={user.subscription_active}
      >
        {children}
      </PlanGate>
    );
  }

  return children;
};

// Logged-in user opened an ?a= link - attach that affiliate (backend decides if allowed)
const claimAffiliate = async (code) => {
  if (!code) return;
  try {
    await fetch(`${authService.API_BASE}/user/affiliate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify({ affiliate: code }),
    });
  } catch (e) { /* ignore */ }
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('review_prompt_dismissed');
    if (dismissed) return;

    const firstVisit = localStorage.getItem('first_visit_date');
    if (!firstVisit) {
      localStorage.setItem('first_visit_date', Date.now().toString());
      return;
    }

    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - parseInt(firstVisit) >= sevenDays) {
      setShowReviewPrompt(true);
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem('uc_checked_asins');
    localStorage.removeItem('uc_checked_sellers');
    localStorage.removeItem('uc_seed_asins');
    localStorage.removeItem('uc_seen_brands');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const a = params.get('a');
    if (a) {
      localStorage.setItem('affiliate_encoded', a.trim());
    }

    const checkAuth = async () => {
      const authResult = await authService.verifyAuth();
      setIsAuthenticated(authResult.valid);
      if (authResult.valid) {
        setUser(authResult.user);
        if (!authResult.user.onboarding_completed) {
          setShowOnboarding(true);
        }
        if (a) claimAffiliate(a.trim());
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Refresh user data to get updated onboarding_completed status
    authService.verifyAuth().then(authResult => {
      if (authResult.valid) {
        setUser(authResult.user);
      }
    });
  };

  const handleLogin = async () => {
    const authResult = await authService.verifyAuth();
    if (authResult.valid) {
      setUser(authResult.user);
      if (!authResult.user.onboarding_completed) {
        setShowOnboarding(true);
      }
      setIsAuthenticated(true);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = "/";
  };

  const hideNavbar = window.location.pathname.startsWith('/kit/');

  // Show loading spinner only on initial load
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {!hideNavbar && (
          <Navbar
            isAuthenticated={isAuthenticated}
            user={user}
            onLogout={handleLogout}
          />
        )}

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage isAuthenticated={isAuthenticated} />} />
          <Route path="/login" element={<LoginPage isAuthenticated={isAuthenticated} onLogin={handleLogin} />} />
          <Route path="/signup" element={<SignupPage isAuthenticated={isAuthenticated} onLogin={handleLogin} />} />
          <Route path="/pricing" element={<PricingPage isAuthenticated={isAuthenticated} user={user} />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/webinars" element={<WebinarsPage />} />
          <Route path="/a2w" element={<A2WPage />} />
          <Route path="/how-i-use-msp" element={<HowIUseMSPPage />} />
          <Route path="/communities" element={<CommunitiesPage />} />
          <Route path="/infhunt" element={<InfluencerHunterPage user={user} />} />
          <Route path="/brandimages" element={<BrandImagesPage user={user} />} />
          <Route path="/brandhunt" element={<BrandHunterPage user={user} />} />
          <Route path="/brands" element={<BrandDashboardPage user={user} />} />
          <Route path="/free-aip-thumbnail-maker" element={<FreeAIPThumbnailMaker user={user} />} />
          <Route path="/free-product-photo-maker" element={<FreeProductPhotoMaker user={user} />} />
          <Route path="/mobile-app" element={<MobileAppPage />} />
          <Route path="/kit/:slug" element={<MediaKitPublicPage />} />
          <Route path="/msp" element={<SetSellerPro />} />
          <Route path="/mip" element={<SetInfluencerPro />} />

          {/* Protected Routes - No Plan Required (just authentication) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user}>
                <DashboardPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute user={user}>
                <Favorites user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute user={user}>
                <SettingsPage onSettingsUpdated={handleLogin} />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Pro Plan Required */}
          <Route
            path="/live-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <LiveScanLandingPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-scan-old"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Unlimited">
                <LiveScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-scan-classic"
            element={
              <LiveScanRetiredPage user={user} />
            }
          />
          <Route
            path="/universal-live-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <UniversalLiveScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/storefront-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <StorefrontScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/a2a-deals"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <A2ADealsPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reverse-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <ReverseScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/msp-gpt"
            element={
              <ProtectedRoute user={user}>
                <MspGptPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wholesale"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Pro">
                <WholesaleScanPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/aip/creator-connections"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Pro">
                <CreatorConnectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aip/storefront-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Pro">
                <AIPStorefrontScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aip/csv-scan"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Pro">
                <AipCsvScanPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aip/my-videos"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Beginner">
                <MyVideosDashboard user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/aip/media-kit/new"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Beginner">
                <CreateMediaKitPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aip/media-kit/:kitId"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Beginner">
                <MediaKitBuilderPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aip/media-kit"
            element={
              <ProtectedRoute user={user} requiredPlan="MIP_Beginner">
                <MyKitsListPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/msp-inventory"
            element={
              <ProtectedRoute user={user} requiredPlan="MSP_Unlimited">
                <MSPInventoryPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {showOnboarding && isAuthenticated && (
          <OnboardingModal onComplete={handleOnboardingComplete} />
        )}

        {showReviewPrompt && (
          <ReviewPromptModal onClose={() => setShowReviewPrompt(false)} />
        )}
      </div>
    </Router>
  );
};

export default App;